import { ReduceStore } from "flux/utils";
import PropTypes from "prop-types";
import typeCheckingStore from "data/util/typeCheckingStore";
import Dispatcher from "Dispatcher";
import DefineActions from "jsmodel/DefineActions";
import dotProp from "dot-prop-immutable";
import HybridConfig from "lib/HybridConfig";
import { auditable } from "audit";
import entityFieldMetaStore, { getFieldInfoComparator } from "data/meta/entityFieldMetaStore";
import { getSelfToSelfMappingDefs } from "data/util/merge/buildSelfToSelfMappingDef";
import ActiveFormActions from "../ActiveFormActions";
import MergeActions from "../MergeActions";

export const FT_CHILD = "CHILD"; // RECORD_MAPPING_TYPE_INSERT
export const FT_GLOBAL_REFERENCE_ONLY = "GLOBAL_REFERENCE_ONLY";
export const FT_GLOBAL_REFERENCE_OR_CREATE = "GLOBAL_REFERENCE_OR_CREATE";
export const FT_MAGIC_ROOT = "MAGIC_ROOT";
// these are historical/legacy
export const MAP_TARGET = "RECORD_MAPPING_TYPE_TARGET";
export const MAP_INSERT_GLOBAL = "RECORD_MAPPING_TYPE_INSERT_GLOBAL";
export const MAP_PLACEHOLDER = "RECORD_MAPPING_TYPE_PLACEHOLDER";
export const MAP_REFERENCE_GLOBAL = "RECORD_MAPPING_TYPE_REFERENCE";

export const ORDER_ITEM_TYPE = {
    FIELD: 1,
    CHILD: 2,
};

const extractFieldMapping = f => {
    const info = {
        fieldKey: f.fieldKey,
        versionInfluencing: f.versionInfluencing,
        enableScript: f.enableScript,
        // one or the other
        sourceFieldKey: f.sourceFieldKey,
        sourceScript: f.sourceScript,
    };
    ["enableScript", "sourceScript"].forEach(sn => {
        if (info[sn] != null) {
            // eslint-disable-next-line no-new-func
            info[sn] = new Function("instance", info[sn]);
            if (HybridConfig.auditScriptlets()) {
                const key = "map" +
                    sn.charAt(0).toUpperCase() +
                    sn.substring(1, sn.length - 6);
                info[sn] = auditable(key, info[sn]);
            }
        }
    });
    return info;
};

function loadMapping(state, def) {
    const targetKey = def.INFO.targetKey;
    const sourceKey = def.INFO.sourceKey;
    const info = {
        targetKey,
        sourceKey,
    };
    info.formOrder = [];

    info.children = def.INFO.children
        ? def.INFO.children.map(c => ({
            edge: extractFieldMapping(c.edge),
            sourceKey: c.sourceKey,
            targetKey: c.targetKey,
            type: c.type,
        }))
        : [];
    info.children.forEach(c =>
        info.formOrder.push({
            shortname: c.edge.fieldKey,
            type: ORDER_ITEM_TYPE.CHILD,
            child: c,
        }));

    info.fields = def.INFO.fields
        ? def.INFO.fields.map(extractFieldMapping)
        : [];
    info.fields.forEach(f =>
        info.formOrder.push({
            shortname: f.fieldKey,
            type: ORDER_ITEM_TYPE.FIELD,
            field: f,
        }));

    // sanity checks
    if (process.env.NODE_ENV !== "production") {
        const sns = info.formOrder.map(it => it.shortname);
        if (!sns.every(it => it)) {
            console.log("unnamed : ", info.formOrder.filter(it =>
                !it.shortname));
            throw new Error("something's missing a shortname");
        }
        const unique = new Set(sns);
        if (unique.size !== sns.length) {
            console.log("shortnames : ", sns.slice().sort());
            console.log("    unique : ", Array.from(unique).sort());
            unique.forEach(sn =>
                sns.splice(sns.indexOf(sn), 1));
            console.log("     dupes : ", sns.slice().sort());
            throw new Error("duplicate shortname(s)?");
        }
    }
    info.formOrder.sort(getFieldInfoComparator(
        entityFieldMetaStore.getInfosByKey(targetKey),
        "shortname"));

    /*
     * Cross tree associations are special in mapping, as they're always
     * implicit, and they live on their nearest common ancestor, not on the
     * owning end. So they aren't part of form order; they're always a
     * separate thing.
     */
    info.crossTrees = def.INFO.crossTrees
        ? def.INFO.crossTrees.map(ct => ({
            fieldKey: ct.fieldKey,
            sourceFieldKey: ct.sourceFieldKey,
            sourceOwnerPath: ct.sourceOwnerPath,
            targetOwnerPath: ct.targetOwnerPath,
        }))
        : [];
    state = dotProp.set(state, ["targets", targetKey, "sources", sourceKey], info);
    return state;
}

// TODO: this key is so we don't rebuild the self to self mapping defs every
// time ActiveFormActions.START_RENDER is dispatched.
const SELF_TO_SELF_KEY = "_selfToSelfLoaded";

class MappingInfoStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {
            targets: {},
            [SELF_TO_SELF_KEY]: false
        };
    }

    reduce(state, action) {
        switch (action.type) {
            case MergeActions.LAUNCH_DIALOG:
            case ActiveFormActions.START_RENDER: {
                if(state[SELF_TO_SELF_KEY]) {
                    return state;
                }
                this.__dispatcher.waitFor([
                    entityFieldMetaStore.getDispatchToken(),
                ]);
                // build all self to self mapping defs
                getSelfToSelfMappingDefs().forEach((def) => {
                    state = loadMapping(state, def);
                });

                state[SELF_TO_SELF_KEY] = true;
                return state;
            }
            case DefineActions.MAPPING:
                this.__dispatcher.waitFor([
                    entityFieldMetaStore.getDispatchToken(),
                ]);
                return loadMapping(state, action.def);
            case DefineActions._UNLOAD:
                return this.getInitialState();
            default:
                return state;
        }
    }

    getInfo(targetKey, sourceKey) {
        const targets = this.getState().targets;
        if (! targets.hasOwnProperty(targetKey)) {
            throw new Error(`No mappings targeting ${targetKey} are known.`);
        }
        const sources = targets[targetKey].sources;
        if (! sources.hasOwnProperty(sourceKey)) {
            throw new Error(`No mapping from ${sourceKey} to ${targetKey} is known.`);
        }
        return sources[sourceKey];
    }

    hasInfo(targetKey, sourceKey) {
        const targets = this.getState().targets;
        if (! targets.hasOwnProperty(targetKey)) {
            return false;
        }
        const sources = targets[targetKey].sources;
        if (! sources.hasOwnProperty(sourceKey)) {
            return false;
        }
        return true;
    }
}

const coreFieldShape = {
    fieldKey: PropTypes.string.isRequired,
    versionInfluencing: PropTypes.bool,
    enableScript: PropTypes.func,
};

const fieldType = PropTypes.oneOfType([
    PropTypes.shape({
        ...coreFieldShape,
        sourceFieldKey: PropTypes.string.isRequired,
    }),
    PropTypes.shape({
        ...coreFieldShape,
        sourceScript: PropTypes.func.isRequired,
    }),
]);

MappingInfoStore.stateTypes = {
    targets: PropTypes.objectOf(PropTypes.shape({
        sources: PropTypes.objectOf(PropTypes.shape({
            sourceKey: PropTypes.string.isRequired,
            targetKey: PropTypes.string.isRequired,
            children: PropTypes.arrayOf(PropTypes.shape({
                edge: PropTypes.oneOfType([
                    fieldType,
                    // neither a script nor a key, which means we're "spreading"
                    // a single source record across a target hierarchy
                    PropTypes.shape(coreFieldShape),
                ]).isRequired,
                sourceKey: PropTypes.string.isRequired,
                targetKey: PropTypes.string.isRequired,
                type: PropTypes.oneOf([
                    FT_CHILD,
                    FT_GLOBAL_REFERENCE_ONLY,
                    FT_GLOBAL_REFERENCE_OR_CREATE,
                    FT_MAGIC_ROOT,
                ]).isRequired,
            })),
            fields: PropTypes.arrayOf(fieldType),
            crossTrees: PropTypes.arrayOf(PropTypes.shape({
                fieldKey: PropTypes.string.isRequired,
                sourceFieldKey: PropTypes.string.isRequired,
                sourceOwnerPath: PropTypes.arrayOf(PropTypes.string).isRequired,
            })),
        })),
    })).isRequired,
};

export default typeCheckingStore(new MappingInfoStore());



// WEBPACK FOOTER //
// ./app/data/meta/MappingInfoStore.js